import React from 'react';
import { Clock, Link2, Trash2, Eye, Download } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const NoteCard = ({ note, onViewDetails, onDelete, onLinkTimeEntry }) => {
  const formatDate = (dateStr) => {
    // Parse date string - handle ISO format and extract date-only value
    let date;
    if (typeof dateStr === 'string') {
      // For meeting_date: extract date part directly (stored at noon UTC to avoid shifts)
      // For created_at: convert UTC to local first, then extract date
      if (dateStr.includes('T')) {
        // Check if this is a meeting_date (stored at noon UTC) or a timestamp (created_at)
        // If the time is around noon (12:00), it's likely a meeting_date, so extract date part
        // Otherwise, it's a timestamp, so convert to local first
        const utcDate = new Date(dateStr);
        const utcHour = utcDate.getUTCHours();
        
        if (utcHour === 12 && utcDate.getUTCMinutes() === 0) {
          // Likely a meeting_date stored at noon UTC - extract date part directly
          const datePart = dateStr.split('T')[0];
          const [year, month, day] = datePart.split('-').map(Number);
          date = new Date(year, month - 1, day);
        } else {
          // Likely a timestamp (created_at) - convert UTC to local first
          const year = utcDate.getFullYear();
          const month = utcDate.getMonth();
          const day = utcDate.getDate();
          date = new Date(year, month, day);
        }
      } else {
        // Already a date-only string
        const [year, month, day] = dateStr.split('-').map(Number);
        date = new Date(year, month - 1, day);
      }
    } else {
      date = new Date(dateStr);
    }
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return ` • ${displayHour}:${minutes} ${ampm}`;
  };

  // Get project color or default
  const projectColor = note.project?.color || note.project_id?.color || '#6B7280';

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-all">
      {/* Header */}
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          {/* Meeting Date */}
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-1 flex items-center gap-1 transition-colors">
            <Clock className="w-4 h-4" />
            <span>
              Meeting: {formatDate(note.meeting_date)}
              {note.meeting_time && formatTime(note.meeting_time)}
            </span>
          </div>

          {/* If backdated, show creation date */}
          {note.is_backdated && (
            <div className="text-xs text-gray-500 dark:text-gray-500 transition-colors">
              📝 Logged: {formatDate(note.created_at)}
            </div>
          )}

          {/* Title */}
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mt-1 transition-colors">
            {note.title}
          </h3>
        </div>
      </div>

      {/* Project Indicator */}
      {note.project && (
        <div className="flex items-center gap-2 mb-3">
          <div 
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: projectColor }}
          />
          <span className="text-sm text-gray-700 dark:text-gray-300 transition-colors">
            {note.project.name}
          </span>
        </div>
      )}

      {/* AI Summary */}
      <div className="mb-3">
        <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors">
          💡 Summary:
        </div>
        <div className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap line-clamp-3 transition-colors">
          {note.summary}
        </div>
      </div>

      {/* Time Entry Link */}
      {note.time_entry_id || note.time_entry ? (
        <div className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 mb-3 transition-colors">
          <Link2 className="w-4 h-4" />
          <span>
            Linked to: {note.time_entry?.duration_minutes 
              ? `${(note.time_entry.duration_minutes / 60).toFixed(1)}h` 
              : 'time entry'}
          </span>
        </div>
      ) : (
        <div className="text-sm text-gray-500 dark:text-gray-500 mb-3 transition-colors">
          🔗 Not linked to time entry
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 pt-3 border-t border-gray-100 dark:border-gray-700 transition-colors">
        <Button
          onClick={() => onViewDetails(note.id)}
          variant="outline"
          size="sm"
          className="flex-1 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <Eye className="w-4 h-4 mr-1" />
          View Details
        </Button>
        
        <Button
          onClick={() => {
            // Export as text file
            const formatDate = (dateStr) => {
              const date = new Date(dateStr);
              return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
              });
            };
            
            const formatTime = (timeStr) => {
              if (!timeStr) return '';
              const [hours, minutes] = timeStr.split(':');
              const hour = parseInt(hours);
              const ampm = hour >= 12 ? 'PM' : 'AM';
              const displayHour = hour % 12 || 12;
              return `${displayHour}:${minutes} ${ampm}`;
            };
            
            const content = `Title: ${note.title}\n\nDate: ${formatDate(note.meeting_date)}\n${note.meeting_time ? `Time: ${formatTime(note.meeting_time)}\n` : ''}${note.project ? `Project: ${note.project.name}\n` : ''}\n\nSummary:\n${note.summary}\n\nFull Transcript:\n${note.transcript || 'N/A'}`;
            
            const blob = new Blob([content], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${note.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${new Date(note.meeting_date).toISOString().split('T')[0]}.txt`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success('Note exported!');
          }}
          variant="outline"
          size="sm"
          title="Export as text file"
          className="text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <Download className="w-4 h-4" />
        </Button>
        
        {!note.time_entry_id && !note.time_entry && (
          <Button
            onClick={() => onLinkTimeEntry(note.id)}
            variant="outline"
            size="sm"
            className="flex items-center gap-1 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <Link2 className="w-4 h-4" />
            Link
          </Button>
        )}
        
        <Button
          onClick={() => onDelete(note.id)}
          variant="outline"
          size="sm"
          className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 border-red-200 dark:border-red-800 transition-colors"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export default NoteCard;

